import React, { useEffect, useState } from 'react'
import { Card, Typography, Avatar, Col, Row, Grid } from 'antd'
import { CalendarFilled, CalendarOutlined, MessageFilled, PhoneOutlined, StarFilled, StarOutlined, UserOutlined } from '@ant-design/icons'
import { MediaDisplay } from 'components/MediaDisplay'
import { useHistory } from 'react-router-dom'
import './PostNewsFeedCard.css'
import { ReactComponent as StarIcon } from './../../assets/new-icons/Star.svg'
import { ReactComponent as StarOutlineIcon } from './../../assets/new-icons/StarOutline.svg'
import { convertToDisplayViewCounter } from 'helper/common'

const { Paragraph, Text } = Typography
const { useBreakpoint } = Grid

// Component hiển thị header với avatar, tên trạm và thời gian, khu vực
const PostHeader = ({ stationsName, introUpdatedAt, stationsAddress, stationsLogo, stationCode, apiKey, introCtaText, stationArea }) => {
  const history = useHistory()

  function formatTimeAgo(dateStr) {
    if (!dateStr) return 'Không xác định'

    const rtf = new Intl.RelativeTimeFormat('vi', { numeric: 'auto' })
    const now = new Date()
    const date = new Date(dateStr)

    if (isNaN(date.getTime())) return 'Không hợp lệ'

    const diffInSeconds = Math.floor((now - date) / 1000)

    const intervals = [
      { unit: 'year', seconds: 31536000 },
      { unit: 'month', seconds: 2592000 },
      { unit: 'week', seconds: 604800 },
      { unit: 'day', seconds: 86400 },
      { unit: 'hour', seconds: 3600 },
      { unit: 'minute', seconds: 60 },
      { unit: 'second', seconds: 1 }
    ]

    for (let i = 0; i < intervals.length; i++) {
      const { unit, seconds } = intervals[i]
      const delta = Math.floor(diffInSeconds / seconds)
      if (delta >= 1) {
        return rtf.format(-delta, unit)
      }
    }

    return 'Vừa xong'
  }

  return (
    <div className="post-header d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center ">
      <div className="d-flex align-items-start w-100 overflow-hidden">
        <Avatar
          size={48}
          src={stationsLogo || process.env.PUBLIC_URL + '/logo-vr.png'}
          style={{ borderRadius: '50%', width: 48, height: 48, objectFit: 'cover', flexShrink: 0, cursor: 'pointer' }}
          onClick={() =>
            history.push(`/station/${stationCode}`, {
              apiKey: apiKey,
              introCtaText: introCtaText
            })
          }
        />
        <div className="ms-3 d-flex flex-column overflow-hidden" style={{ minWidth: 0 }}>
          <div
            className="fw-bold text-truncate station-name"
            style={{ maxWidth: '100%', cursor: 'pointer' }}
            title={stationsName}
            onClick={() =>
              history.push(`/station/${stationCode}`, {
                apiKey: apiKey,
                introCtaText: introCtaText
              })
            }>
            {stationsName}
          </div>
          <div className="small">
            {introUpdatedAt && `${formatTimeAgo(introUpdatedAt)} • `}
            <a
              className="location"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(stationsAddress)}`}
              target="_blank"
              rel="noopener noreferrer">
              {stationArea}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

// Component mô tả có "Xem thêm / Thu gọn"
const ExpandableDescription = ({ content }) => {
  const [expanded, setExpanded] = useState(false)
  const [isEllipsis, setIsEllipsis] = useState(false)

  return (
    <div className="post-description">
      <Paragraph
        ellipsis={
          !expanded
            ? {
                rows: 1,
                expandable: false,
                onEllipsis: (ellipsis) => setIsEllipsis(ellipsis)
              }
            : false
        }>
        {content || ''}
      </Paragraph>
      {isEllipsis && (
        <Text className="text-secondary fw-bold read-more" style={{ cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Thu gọn' : 'Xem thêm'}
        </Text>
      )}
    </div>
  )
}

const RenderStar = (totalRate) => {
  const list = []
  for (let index = 1; index <= 5; index++) {
    const Icon = index <= totalRate ? StarIcon : StarOutlineIcon
    list.push(<Icon key={index} style={{ height: 16, width: 16 }} />)
  }

  return <div className="d-flex ai-c j-sb w-100">{list}</div>
}

// Component hiển thị lượt xem và lượt click trên 1 dòng, nằm bên phải
const PostStats = ({ ratingPoint, countReview = 0, totalCustomerRecordCount = 0, totalCustomerScheduleCount = 0 }) => {
  return (
    <div className="post-stat w-100">
      <Row gutter={[16, 16]} align="middle" justify="space-between">
        {/* Right side: rating star */}
        <Col xs={6} sm={6} md={6} lg={6} xl={6}>
          <div className="d-flex justify-content-start">
            {RenderStar(ratingPoint)}
            <span className="ms-1">{ratingPoint.toFixed(1)}</span>
          </div>
        </Col>

        {/* Left side: 3 items */}
        <Col xs={18} sm={18} md={18} lg={18} xl={18}>
          <div className="d-flex flex-wrap gap-12 justify-content-end">
            <div className="d-flex flex-wrap gap-12 justify-content-end">
              <div className="d-flex gap-2 align-items-center">
                <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0 8.25C0 3.83172 3.58172 0.25 8 0.25C12.4183 0.25 16 3.83172 16 8.25C16 12.6683 12.4183 16.25 8 16.25C3.58172 16.25 0 12.6683 0 8.25Z"
                    fill="url(#paint0_linear_436_74)"
                  />
                  <path
                    d="M7.77404 3.37451C7.86647 3.18724 8.13351 3.18724 8.22594 3.37451L9.50879 5.97385C9.54549 6.04822 9.61644 6.09976 9.69851 6.11169L12.5671 6.52851C12.7737 6.55855 12.8562 6.81252 12.7067 6.9583L10.631 8.9816C10.5716 9.03949 10.5445 9.12289 10.5585 9.20463L11.0485 12.0616C11.0838 12.2674 10.8678 12.4244 10.6829 12.3272L8.11724 10.9783C8.04384 10.9397 7.95614 10.9397 7.88274 10.9783L5.31703 12.3272C5.13218 12.4244 4.91614 12.2674 4.95144 12.0616L5.44145 9.20463C5.45547 9.12289 5.42837 9.03949 5.36898 8.9816L3.29328 6.9583C3.14373 6.81252 3.22626 6.55855 3.43293 6.52851L6.30147 6.11169C6.38354 6.09977 6.45449 6.04822 6.49119 5.97385L7.77404 3.37451Z"
                    fill="white"
                  />
                  <defs>
                    <linearGradient id="paint0_linear_436_74" x1="7.689e-08" y1="7.05" x2="16" y2="7.05" gradientUnits="userSpaceOnUse">
                      <stop stop-color="#0F64E4" />
                      <stop offset="1" stop-color="#262FED" />
                    </linearGradient>
                  </defs>
                </svg>
                <Text>{convertToDisplayViewCounter(countReview)}</Text>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0 8.25C0 3.83172 3.58172 0.25 8 0.25C12.4183 0.25 16 3.83172 16 8.25C16 12.6683 12.4183 16.25 8 16.25C3.58172 16.25 0 12.6683 0 8.25Z"
                    fill="url(#paint0_linear_436_78)"
                  />
                  <path
                    d="M10.1111 4.4989V3.80556C10.1111 3.62334 9.95998 3.47223 9.77776 3.47223C9.59554 3.47223 9.44443 3.62334 9.44443 3.80556V4.47223H6.55554V3.80556C6.55554 3.62334 6.40443 3.47223 6.22221 3.47223C6.03998 3.47223 5.88887 3.62334 5.88887 3.80556V4.4989C4.68887 4.61001 4.10665 5.32556 4.01776 6.38778C4.00887 6.51667 4.11554 6.62334 4.23998 6.62334H11.76C11.8889 6.62334 11.9955 6.51223 11.9822 6.38778C11.8933 5.32556 11.3111 4.61001 10.1111 4.4989Z"
                    fill="white"
                  />
                  <path
                    d="M11.5556 7.29001H4.44444C4.2 7.29001 4 7.49001 4 7.73445V10.4722C4 11.8056 4.66667 12.6945 6.22222 12.6945H9.77778C11.3333 12.6945 12 11.8056 12 10.4722V7.73445C12 7.49001 11.8 7.29001 11.5556 7.29001ZM6.76 11.01C6.73778 11.0278 6.71556 11.05 6.69333 11.0633C6.66667 11.0811 6.64 11.0945 6.61333 11.1033C6.58667 11.1167 6.56 11.1256 6.53333 11.13C6.50222 11.1345 6.47556 11.1389 6.44444 11.1389C6.38667 11.1389 6.32889 11.1256 6.27556 11.1033C6.21778 11.0811 6.17333 11.05 6.12889 11.01C6.04889 10.9256 6 10.81 6 10.6945C6 10.5789 6.04889 10.4633 6.12889 10.3789C6.17333 10.3389 6.21778 10.3078 6.27556 10.2856C6.35556 10.25 6.44444 10.2411 6.53333 10.2589C6.56 10.2633 6.58667 10.2722 6.61333 10.2856C6.64 10.2945 6.66667 10.3078 6.69333 10.3256C6.71556 10.3433 6.73778 10.3611 6.76 10.3789C6.84 10.4633 6.88889 10.5789 6.88889 10.6945C6.88889 10.81 6.84 10.9256 6.76 11.01ZM6.76 9.45445C6.67556 9.53445 6.56 9.58334 6.44444 9.58334C6.32889 9.58334 6.21333 9.53445 6.12889 9.45445C6.04889 9.37001 6 9.25445 6 9.1389C6 9.02334 6.04889 8.90779 6.12889 8.82334C6.25333 8.6989 6.44889 8.6589 6.61333 8.73001C6.67111 8.75223 6.72 8.78334 6.76 8.82334C6.84 8.90779 6.88889 9.02334 6.88889 9.1389C6.88889 9.25445 6.84 9.37001 6.76 9.45445ZM8.31556 11.01C8.23111 11.09 8.11556 11.1389 8 11.1389C7.88444 11.1389 7.76889 11.09 7.68444 11.01C7.60444 10.9256 7.55556 10.81 7.55556 10.6945C7.55556 10.5789 7.60444 10.4633 7.68444 10.3789C7.84889 10.2145 8.15111 10.2145 8.31556 10.3789C8.39556 10.4633 8.44445 10.5789 8.44445 10.6945C8.44445 10.81 8.39556 10.9256 8.31556 11.01ZM8.31556 9.45445C8.29333 9.47223 8.27111 9.49001 8.24889 9.50779C8.22222 9.52556 8.19556 9.5389 8.16889 9.54779C8.14222 9.56112 8.11556 9.57001 8.08889 9.57445C8.05778 9.5789 8.03111 9.58334 8 9.58334C7.88444 9.58334 7.76889 9.53445 7.68444 9.45445C7.60444 9.37001 7.55556 9.25445 7.55556 9.1389C7.55556 9.02334 7.60444 8.90779 7.68444 8.82334C7.72444 8.78334 7.77333 8.75223 7.83111 8.73001C7.99556 8.6589 8.19111 8.6989 8.31556 8.82334C8.39556 8.90779 8.44445 9.02334 8.44445 9.1389C8.44445 9.25445 8.39556 9.37001 8.31556 9.45445ZM9.87111 11.01C9.78667 11.09 9.67111 11.1389 9.55556 11.1389C9.44 11.1389 9.32445 11.09 9.24 11.01C9.16 10.9256 9.11111 10.81 9.11111 10.6945C9.11111 10.5789 9.16 10.4633 9.24 10.3789C9.40445 10.2145 9.70667 10.2145 9.87111 10.3789C9.95111 10.4633 10 10.5789 10 10.6945C10 10.81 9.95111 10.9256 9.87111 11.01ZM9.87111 9.45445C9.84889 9.47223 9.82667 9.49001 9.80445 9.50779C9.77778 9.52556 9.75111 9.5389 9.72445 9.54779C9.69778 9.56112 9.67111 9.57001 9.64444 9.57445C9.61333 9.5789 9.58222 9.58334 9.55556 9.58334C9.44 9.58334 9.32445 9.53445 9.24 9.45445C9.16 9.37001 9.11111 9.25445 9.11111 9.1389C9.11111 9.02334 9.16 8.90779 9.24 8.82334C9.28445 8.78334 9.32889 8.75223 9.38667 8.73001C9.46667 8.69445 9.55556 8.68556 9.64444 8.70334C9.67111 8.70779 9.69778 8.71668 9.72445 8.73001C9.75111 8.7389 9.77778 8.75223 9.80445 8.77001C9.82667 8.78779 9.84889 8.80556 9.87111 8.82334C9.95111 8.90779 10 9.02334 10 9.1389C10 9.25445 9.95111 9.37001 9.87111 9.45445Z"
                    fill="white"
                  />
                  <defs>
                    <linearGradient id="paint0_linear_436_78" x1="7.689e-08" y1="7.05" x2="16" y2="7.05" gradientUnits="userSpaceOnUse">
                      <stop stop-color="#0F64E4" />
                      <stop offset="1" stop-color="#262FED" />
                    </linearGradient>
                  </defs>
                </svg>
                <Text>{convertToDisplayViewCounter(totalCustomerScheduleCount)}</Text>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0 8.25C0 3.83172 3.58172 0.25 8 0.25C12.4183 0.25 16 3.83172 16 8.25C16 12.6683 12.4183 16.25 8 16.25C3.58172 16.25 0 12.6683 0 8.25Z"
                    fill="url(#paint0_linear_436_87)"
                  />
                  <path
                    d="M8 3.80557C6.83555 3.80557 5.88889 4.75224 5.88889 5.91668C5.88889 7.05891 6.78222 7.98335 7.94666 8.02335C7.98222 8.01891 8.01777 8.01891 8.04444 8.02335C8.05333 8.02335 8.05777 8.02335 8.06666 8.02335C8.07111 8.02335 8.07111 8.02335 8.07555 8.02335C9.21333 7.98335 10.1067 7.05891 10.1111 5.91668C10.1111 4.75224 9.16444 3.80557 8 3.80557Z"
                    fill="white"
                  />
                  <path
                    d="M10.2578 9.20557C9.01779 8.3789 6.99557 8.3789 5.74668 9.20557C5.18224 9.58335 4.87112 10.0945 4.87112 10.6411C4.87112 11.1878 5.18224 11.6945 5.74224 12.0678C6.36446 12.4856 7.18224 12.6945 8.00001 12.6945C8.81779 12.6945 9.63557 12.4856 10.2578 12.0678C10.8178 11.69 11.1289 11.1833 11.1289 10.6322C11.1245 10.0856 10.8178 9.5789 10.2578 9.20557Z"
                    fill="white"
                  />
                  <defs>
                    <linearGradient id="paint0_linear_436_87" x1="7.689e-08" y1="7.05" x2="16" y2="7.05" gradientUnits="userSpaceOnUse">
                      <stop stop-color="#0F64E4" />
                      <stop offset="1" stop-color="#262FED" />
                    </linearGradient>
                  </defs>
                </svg>
                <Text>{convertToDisplayViewCounter(totalCustomerRecordCount)}</Text>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  )
}

const InteractionBar = ({ stationCode, apiKey, introCtaText, functionCTA, setIsRate }) => {
  const history = useHistory()
  const items = [
    {
      icon: <StarOutlined />,
      label: 'Đánh giá',
      action: () => {
        setIsRate(true)
      }
    },
    { icon: <CalendarOutlined />, label: 'Đặt lịch', action: functionCTA },
    { icon: <PhoneOutlined />, label: 'Liên hệ', action: () => history.push(`/station/${stationCode}`, { apiKey, introCtaText }) }
  ]

  return (
    <div className="container">
      <div className="row text-center py-2">
        {items.map((item, index) => (
          <div onClick={item.action} className="interaction-item col-4 d-flex gap-2 justify-content-center align-items-center" key={index}>
            <div style={{ fontSize: 20, color: 'gray' }}>{item.icon}</div>
            <div style={{ fontSize: 14, color: '#0f1b4c' }}>{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

const PostNewsFeedCard = ({ post, functionCTA, setDetail, setIsRate }) => {
  const {
    // thông tin trạm
    stationsAddress = '',
    stationArea = '',
    stationsName = '',
    stationsLogo = '',
    stationCode = '',
    stationsBanner,
    // thông tin bài viết
    introContent = '',
    introUpdatedAt = '',
    introCtaText = '',
    stationLabelCTA = '',
    introMedia = '',
    apiKey,
    // tương tác
    countReview = 0,
    ratingPoint = 0,
    totalCustomerRecordCount = 0,
    totalCustomerScheduleCount = 0
  } = post
  const [media, setMedia] = useState({})

  useEffect(() => {
    if (introMedia?.items?.length > 0) {
      setMedia(introMedia)
    } else {
      if (stationsBanner) {
        setMedia({
          type: 'IMAGE',
          items: [{ url: stationsBanner }]
        })
      }
    }
  }, [stationsBanner, introMedia])

  return (
    <Card
      className="post-card p-0"
      onClick={() => {
        setDetail(post)
      }}>
      <PostHeader
        introCtaText={introCtaText}
        apiKey={apiKey}
        stationsName={stationsName}
        introUpdatedAt={introUpdatedAt}
        stationsAddress={stationsAddress}
        stationArea={stationArea}
        stationsLogo={stationsLogo}
        stationCode={stationCode}
      />

      <ExpandableDescription content={introContent} />

      <MediaDisplay media={media} />
      <div>
        <div className="bg-secondary">
          <div className="d-flex justify-content-between align-items-center post-information">
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <Paragraph ellipsis={{ rows: 2, expandable: false, tooltip: true }} className="mb-0 fw-bold">
                {stationLabelCTA || ''}
              </Paragraph>
            </div>
            <div style={{ whiteSpace: 'nowrap' }}>
              {introCtaText && apiKey ? (
                <button className="btn cta-button fw-bold rounded-sm" onClick={functionCTA}>
                  {introCtaText}
                </button>
              ) : (
                <button className="btn cta-button fw-bold rounded-sm" onClick={functionCTA}>
                  <MessageFilled className="mr-2" />
                  Liên hệ ngay
                </button>
              )}
            </div>
          </div>
        </div>
        <div style={{ height: '1px' }} className="bg-line-separate"></div>
        <PostStats
          ratingPoint={ratingPoint}
          countReview={countReview}
          totalCustomerScheduleCount={totalCustomerScheduleCount}
          totalCustomerRecordCount={totalCustomerRecordCount}
        />
        <div style={{ height: '1px' }} className="bg-line-separate"></div>
        <InteractionBar
          setDetail={setDetail}
          setIsRate={setIsRate}
          stationCode={stationCode}
          apiKey={apiKey}
          introCtaText={introCtaText}
          functionCTA={functionCTA}
        />
      </div>
      {/* <PaymentConfirmationModal isOpen={isModalVisible} onCancel={() => setIsModalVisible(false)} link={link} /> */}
    </Card>
  )
}

export default PostNewsFeedCard
